/* One explicit add, then fresh Shopify-rendered cart HTML. Never retry a POST. */
(() => {
  async function addRecommendation({ form, drawer, pageUrl, opener }, environment = globalThis) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
      throw new Error('Your cart is still loading. Please try again in a moment.');
    }
    const page = new URL(pageUrl);
    const endpoint = new URL(form.action, page);
    if (endpoint.origin !== page.origin || !/\/cart\/add(?:\.js)?\/?$/.test(endpoint.pathname)) {
      throw new Error('Unable to connect to this store’s cart.');
    }
    endpoint.pathname = endpoint.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    const descriptors = drawer.getSectionsToRender();
    const sections = [...new Set(descriptors.map(section => section.id))];
    if (!sections.length || sections.length > 5) throw new Error('Please refresh the page before adding this item.');
    const body = new environment.FormData(form);
    body.set('quantity', '1');
    body.set('sections', sections.join(','));
    body.set('sections_url', page.pathname + page.search);
    const valid = html => descriptors.every(section => typeof html?.[section.id] === 'string' &&
      new environment.DOMParser().parseFromString(html[section.id], 'text/html').querySelector(section.selector || '.shopify-section'));
    let added = false;
    let rejected = '';
    try {
      const response = await environment.fetch(endpoint.href, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        rejected = typeof state.description === 'string' ? state.description : typeof state.message === 'string' ? state.message : '';
        throw new Error('Cart add could not be confirmed.');
      }
      added = true;
      if (!valid(state.sections)) {
        const refreshUrl = new URL(page);
        refreshUrl.searchParams.set('sections', sections.join(','));
        const refreshed = await environment.fetch(refreshUrl.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!refreshed.ok) throw new Error('Cart refresh unavailable.');
        state.sections = await refreshed.json();
      }
      if (!valid(state.sections)) throw new Error('Cart refresh unavailable.');
      drawer.setActiveElement?.(opener);
      drawer.renderContents(state);
      drawer.classList.remove('is-empty');
      return state;
    } catch (failure) {
      const error = new Error(added
        ? 'Added to your cart, but the drawer could not refresh. Please check your cart before adding again.'
        : (rejected ? rejected + ' ' : 'We could not confirm the add. ') + 'Please check your cart before trying again.');
      error.cartAdded = added;
      error.checkCart = true;
      throw error;
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { addRecommendation };
  if (typeof customElements === 'undefined' || customElements.get('aven-cart-recommendations')) return;
  class AvenCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-rec-select]');
        if (!select) return;
        const card = select.closest('[data-rec-card]');
        const selected = select.selectedOptions[0];
        if (!card || !selected) return;
        card.querySelector('[data-rec-price]').textContent = selected.dataset.money;
        const compare = card.querySelector('[data-rec-compare]');
        compare.textContent = selected.dataset.compareMoney;
        compare.hidden = selected.dataset.onSale !== 'true';
        const image = card.querySelector('.aven-cart-rec__image');
        if (image && selected.dataset.image) {
          image.removeAttribute('srcset');
          image.src = selected.dataset.image;
        }
      }, { signal: this.abort.signal });
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-rec-form]');
        if (!form) return;
        event.preventDefault();
        this.add(form);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
    async add(form) {
      if (this.busy) return;
      const select = form.querySelector('[data-rec-select]');
      if (!select?.value || !select.selectedOptions[0] || select.selectedOptions[0].disabled) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const button = form.querySelector('[data-rec-submit]');
      const buttonText = button.querySelector('[data-rec-button-text]');
      const status = this.querySelector('[data-rec-status]');
      const recovery = this.querySelector('[data-rec-recovery]');
      status.hidden = true;
      recovery.hidden = true;
      buttonText.textContent = 'Adding…';
      // Build the payload before disabling any select: disabled controls are
      // excluded from FormData. Keep all controls read-only through this add.
      const chosenId = select.value;
      const chosenAction = form.action;
      const controls = [...this.querySelectorAll('button, select')];
      controls.forEach(control => { control.disabled = true; });
      let confirmedAdded = false;
      try {
        const payloadForm = document.createElement('form');
        payloadForm.action = chosenAction;
        const variantInput = document.createElement('input');
        variantInput.name = 'id';
        variantInput.value = chosenId;
        payloadForm.append(variantInput);
        const drawer = this.closest('cart-drawer');
        const opener = document.getElementById('cart-icon-bubble');
        await addRecommendation({ form: payloadForm, drawer, pageUrl: window.location.href, opener });
        confirmedAdded = true;
        // The old card has been replaced by the server-rendered drawer. Put
        // keyboard focus on checkout, not the now-removed recommendation.
        const checkout = drawer.querySelector('[name="checkout"]');
        if (checkout && drawer.classList.contains('active')) checkout.focus({ preventScroll: true });
      } catch (error) {
        confirmedAdded = !!error.cartAdded;
        status.textContent = error.message || 'Please check your cart before trying again.';
        status.hidden = false;
        recovery.hidden = !error.checkCart;
      } finally {
        if (this.isConnected) {
          this.removeAttribute('aria-busy');
          controls.forEach(control => { control.disabled = false; });
          buttonText.textContent = confirmedAdded ? 'Added' : 'Add';
          if (confirmedAdded) button.disabled = true;
          this.busy = false;
        }
      }
    }
  }
  customElements.define('aven-cart-recommendations', AvenCartRecommendations);
})();
